import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type ThemeMode = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

function getSystemTheme(): ResolvedTheme {
  if (typeof window === 'undefined') return 'dark';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function resolveThemeMode(mode: ThemeMode): ResolvedTheme {
  return mode === 'system' ? getSystemTheme() : mode;
}

interface ThemeState {
  appearance: ThemeMode;
  resolvedAppearance: ResolvedTheme;
  setAppearance: (appearance: ThemeMode) => void;
  setResolvedAppearance: (resolved: ResolvedTheme) => void;
  toggleAppearance: () => void;
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      appearance: 'dark',
      resolvedAppearance: 'dark',
      setAppearance: (appearance) =>
        set({
          appearance,
          resolvedAppearance: resolveThemeMode(appearance),
        }),
      setResolvedAppearance: (resolvedAppearance) => set({ resolvedAppearance }),
      toggleAppearance: () => {
        const next = get().resolvedAppearance === 'dark' ? 'light' : 'dark';
        set({ appearance: next, resolvedAppearance: next });
      },
    }),
    {
      name: 'prototype-theme',
      partialize: (state) => ({ appearance: state.appearance }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.resolvedAppearance = resolveThemeMode(state.appearance);
        }
      },
    }
  )
);
